/**
 * ChatWindow Component
 *
 * Main chat interface with header, messages, and input.
 * Phase 7: Frontend Integration
 *
 * @module ChatWindow
 */

'use client';

import { useState, useCallback } from 'react';
import {
  Box,
  VStack,
  Flex,
  IconButton,
  Heading,
  Text,
  Button,
  Tooltip,
  useStandaloneConfirm,
} from '@axon/ui';
import { useTranslations, useLocale } from 'next-intl';
import {
  LuX,
  LuMinus,
  LuTrash2,
  LuMaximize2,
  LuMinimize2,
  LuGraduationCap,
  LuBookOpen,
  LuDownload,
} from 'react-icons/lu';

import { useChatbotContext } from '../providers/ChatbotProvider';
import type { ChatWindowSize } from '../types/chatbot.types';
import { CHAT_WINDOW_SIZES } from '../types/chatbot.types';

import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from './TypingIndicator';
import { SuggestionChips } from './SuggestionChips';
import { ProactiveSuggestionBadge } from './ProactiveSuggestionBadge';
import { TrainingPanel } from './TrainingPanel';
import { SopPanel } from './SopPanel';
import { ConnectionStatusBadge } from './ConnectionStatusBadge';

// ============================================================================
// Types
// ============================================================================

type TabMode = 'chat' | 'training' | 'sop';

// ============================================================================
// Constants
// ============================================================================

// Tab mode is intentionally NOT persisted — always open on 'chat' tab for immediate textbox visibility

// ============================================================================
// Component
// ============================================================================

/**
 * ChatWindow component
 *
 * Full chat interface with header, messages, and input.
 *
 * @example
 * <ChatWindow />
 */
export function ChatWindow() {
  const {
    closeChat,
    minimizeChat,
    clearMessages,
    reconnect,
    session,
    messages,
    error,
    isConnected,
    isConnecting,
  } = useChatbotContext();
  const t = useTranslations('chatbot');
  const locale = useLocale();
  const { confirm, confirmDialog } = useStandaloneConfirm();
  const [windowSize, setWindowSize] = useState<ChatWindowSize>('default');

  const [tabMode, setTabMode] = useState<TabMode>('chat');

  const dimensions = CHAT_WINDOW_SIZES[windowSize];
  const isExpanded = windowSize === 'expanded';

  const toggleWindowSize = useCallback(() => {
    setWindowSize((prev) => (prev === 'default' ? 'expanded' : 'default'));
  }, []);

  const setTab = useCallback((mode: TabMode) => {
    setTabMode(mode);
  }, []);

  // Connection status (socket-level, from context — distinct from the session
  // flag which only tracks the chat conversation).
  const isTyping = session?.isTyping ?? false;

  const handleExportChat = () => {
    const payload = {
      conversationId: session?.conversationId ?? null,
      language: session?.language ?? 'en',
      exportedAt: new Date().toISOString(),
      messageCount: messages.length,
      messages,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const convSlice = (session?.conversationId ?? 'unknown').slice(0, 8);
    const timestamp = Date.now();
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-debug-${convSlice}-${timestamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClearMessages = async () => {
    const confirmed = await confirm({
      title: t('confirm.clearTitle'),
      description: t('confirm.clearMessages'),
      confirmText: t('confirm.clear'),
      cancelText: t('confirm.cancel'),
      isDestructive: true,
    });
    if (!confirmed) return;
    clearMessages();
  };

  // a11y: Escape closes the window. Skips when the event was already handled
  // (ChatInput preventDefaults Escape to cancel voice recording); the confirm
  // dialog lives in a focus-trapped portal so its Escape never reaches here.
  const handleWindowKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        closeChat();
      }
    },
    [closeChat]
  );

  return (
    <Box
      data-testid="chat-window"
      onKeyDown={handleWindowKeyDown}
      // Mobile (base): fill the full-screen container ChatWidget provides.
      // md+: fixed floating-panel dimensions from CHAT_WINDOW_SIZES.
      width={{ base: '100%', md: `${dimensions.width}px` }}
      height={{ base: '100%', md: `${dimensions.height}px` }}
      maxHeight={{ base: '100%', md: 'calc(100vh - 80px)' }}
      bg="bg.panel"
      borderRadius={{ base: 'none', md: 'xl' }}
      boxShadow="2xl"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      border="1px solid"
      borderColor="border.default"
      transition="width 0.2s ease, height 0.2s ease"
    >
      {/* Header */}
      <Flex
        colorPalette="blue"
        paddingX="3"
        paddingY="2"
        bg="colorPalette.solid"
        color="colorPalette.contrast"
        align="center"
        justify="between"
        flexShrink={0}
        overflow="visible"
      >
        <VStack align="flex-start" gap="0" minWidth={0} flex="1">
          <Heading size="sm" truncate>
            {t('title')}
          </Heading>
          <Flex align="center" gap="1">
            <ConnectionStatusBadge isConnected={isConnected} isConnecting={isConnecting} />
            <Text fontSize="xs" opacity="0.8">
              {locale === 'tr' ? 'TR' : 'EN'}
            </Text>
          </Flex>
        </VStack>

        <Flex gap="0.5" align="center" flexShrink={0}>
          {/* Proactive suggestions (Phase 18) */}
          <ProactiveSuggestionBadge />

          {/* SOP toggle (Feature #54) — aria-labels were hardcoded
              English; now localized and surfaced as tooltips (the
              7-icon header was unguessable from icons alone). */}
          <Tooltip content={tabMode === 'sop' ? t('backToChat') : t('sopLibrary')} asChild>
            <IconButton
              aria-label={tabMode === 'sop' ? t('backToChat') : t('sopLibrary')}
              onClick={() => setTab(tabMode === 'sop' ? 'chat' : 'sop')}
              size="sm"
              variant={tabMode === 'sop' ? 'primary' : 'ghost'}
              icon={<LuBookOpen size={16} />}
            />
          </Tooltip>

          {/* Training toggle (Feature #56) */}
          <Tooltip
            content={tabMode === 'training' ? t('backToChat') : t('training.title')}
            asChild
          >
            <IconButton
              aria-label={tabMode === 'training' ? t('backToChat') : t('training.title')}
              onClick={() => setTab(tabMode === 'training' ? 'chat' : 'training')}
              size="sm"
              variant={tabMode === 'training' ? 'primary' : 'ghost'}
              icon={<LuGraduationCap size={16} />}
            />
          </Tooltip>

          {/* Clear messages */}
          {messages.length > 0 && (
            <Tooltip content={t('clearMessages')} asChild>
              <IconButton
                aria-label={t('clearMessages')}
                icon={<LuTrash2 size={14} />}
                size="xs"
                variant="ghost"
                onClick={() => void handleClearMessages()}
              />
            </Tooltip>
          )}

          {/* Export chat (debug) */}
          {messages.length > 0 && (
            <Tooltip content={t('exportChat')} asChild>
              <IconButton
                aria-label={t('exportChat')}
                icon={<LuDownload size={14} />}
                size="xs"
                variant="ghost"
                onClick={handleExportChat}
              />
            </Tooltip>
          )}

          {/* Expand/Collapse */}
          <Tooltip content={isExpanded ? t('collapse') : t('expand')} asChild>
            <IconButton
              aria-label={isExpanded ? t('collapse') : t('expand')}
              icon={isExpanded ? <LuMinimize2 size={14} /> : <LuMaximize2 size={14} />}
              size="xs"
              variant="ghost"
              onClick={toggleWindowSize}
            />
          </Tooltip>

          {/* Minimize */}
          <Tooltip content={t('minimize')} asChild>
            <IconButton
              aria-label={t('minimize')}
              icon={<LuMinus size={14} />}
              size="xs"
              variant="ghost"
              onClick={minimizeChat}
            />
          </Tooltip>

          {/* Close */}
          <Tooltip content={t('close')} asChild>
            <IconButton
              aria-label={t('close')}
              icon={<LuX size={14} />}
              size="xs"
              variant="ghost"
              onClick={closeChat}
            />
          </Tooltip>
        </Flex>
      </Flex>

      {/* Error banner. The shared @axon/ui Alert uses hardcoded light-mode hex
          colors, so semantic colorPalette tokens are used here to stay
          dark-mode correct. */}
      {error && (
        <Box
          role="alert"
          colorPalette="red"
          paddingX="4"
          paddingY="2"
          bg="colorPalette.subtle"
          color="colorPalette.fg"
          fontSize="sm"
          borderBottom="1px solid"
          borderColor="colorPalette.emphasized"
          flexShrink={0}
        >
          {error}
          {!isConnected && (
            <Box marginLeft="3" display="inline-block">
              <Button size="xs" variant="outline" onClick={() => void reconnect()}>
                {t('reconnect')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* SOP mode (Feature #54) - always mounted to preserve state */}
      <Box
        flex="1"
        overflow="hidden"
        display={tabMode === 'sop' ? 'flex' : 'none'}
        flexDirection="column"
      >
        <SopPanel />
      </Box>

      {/* Training mode (Feature #56) - always mounted to preserve session state */}
      <Box
        flex="1"
        overflow="hidden"
        display={tabMode === 'training' ? 'flex' : 'none'}
        flexDirection="column"
      >
        <TrainingPanel />
      </Box>

      {/* Chat mode - always mounted to preserve scroll position */}
      <Box
        flex="1"
        overflow="hidden"
        display={tabMode === 'chat' ? 'flex' : 'none'}
        flexDirection="column"
      >
        {/* Messages */}
        <Box flex="1" overflow="hidden">
          <MessageList />
        </Box>

        {/* Typing indicator */}
        {isTyping && <TypingIndicator />}

        {/* Suggestions (only when no messages) */}
        {messages.length === 0 && <SuggestionChips />}

        {/* Input */}
        <ChatInput />
      </Box>

      {/* Confirm dialog portal */}
      {confirmDialog}
    </Box>
  );
}

export default ChatWindow;
